// AirConditioning.js - Ares condicionados Mitsubishi, via MELCloud
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wind, Power, Thermometer, ArrowLeft, RefreshCw, AlertCircle,
} from 'lucide-react';
import apiService from '../apiService';

// A MELCloud demora a refletir um comando; reler logo a seguir traria o
// estado anterior e o botão parecia não ter funcionado.
const ESPERA_APOS_COMANDO_MS = 2000;

// Os códigos de modo são da MELCloud, não nossos. O passo de temperatura de
// meio grau também é o que a API aceita.
const MODOS = [
  { modo: 3, icone: '❄️', chave: 'frio' },
  { modo: 1, icone: '🔥', chave: 'calor' },
  { modo: 2, icone: '💧', chave: 'seco' },
  { modo: 7, icone: '🌀', chave: 'ventilador' },
];
const VELOCIDADES = [
  { velocidade: 0, chave: 'auto' },
  { velocidade: 1, chave: 'baixo' },
  { velocidade: 2, chave: 'medio' },
  { velocidade: 3, chave: 'alto' },
];
const PASSO_TEMPERATURA = 0.5;

function AirConditioning({ onBack }) {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDevices = useCallback(async () => {
    try {
      const data = await apiService.getDevices();
      if (data.devices && Array.isArray(data.devices)) {
        setDevices(data.devices);
        setError(data.devices.length === 0 ? t('erros.semDispositivos') : '');
      } else {
        setError(t('erros.respostaInvalida'));
      }
    } catch (err) {
      setError(err.message || t('erros.buscarDispositivos'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const controlar = async (device, comando) => {
    try {
      await apiService.controlDevice(device.device_id, device.building_id, comando);
      setTimeout(fetchDevices, ESPERA_APOS_COMANDO_MS);
    } catch (err) {
      setError(err.message || t('erros.enviarComando'));
    }
  };

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  if (loading) {
    return (
      <div className="loading-container">
        <RefreshCw size={64} className="spin" />
        <p>{t('ac.aCarregar')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <button className="back-button" onClick={onBack}>
            <ArrowLeft size={20} />
            {t('comum.voltar')}
          </button>
          <div className="header-left">
            <Wind size={32} />
            <div>
              <h1>{t('ac.titulo')}</h1>
              <p>{t('ac.subtitulo')}</p>
            </div>
          </div>
          <button onClick={fetchDevices} className="btn-refresh">
            <RefreshCw size={16} />
            {t('comum.atualizar')}
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {devices.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={64} className="spin" />
            <p>{t('ac.aCarregarDispositivos')}</p>
          </div>
        ) : (
          <div className="devices-grid">
            {devices.map((device) => (
              <div key={device.device_id} className={`device-card ${device.power ? 'active' : 'inactive'}`}>
                <div className="device-header">
                  <div className="device-title">
                    <Wind size={24} />
                    <div><h2>{device.device_name}</h2></div>
                  </div>
                  <button
                    onClick={() => controlar(device, { power: !device.power })}
                    className={`power-button ${device.power ? 'on' : 'off'}`}
                  >
                    <Power size={16} />
                    {device.power ? t('comum.ligado') : t('comum.desligado')}
                  </button>
                </div>

                <div className={`temperature-control ${!device.power ? 'disabled' : ''}`}>
                  <div className="temp-display">
                    <div className="temp-item">
                      <div className="temp-label">{t('ac.atual')}</div>
                      <div className="temp-value">
                        <Thermometer size={20} />
                        <span>{device.room_temperature}°C</span>
                      </div>
                    </div>
                    <div className="temp-item">
                      <div className="temp-label">{t('ac.alvo')}</div>
                      <div className="temp-value">
                        <span>{device.set_temperature}°C</span>
                      </div>
                    </div>
                  </div>

                  <div className="temp-buttons">
                    <button
                      onClick={() => controlar(device, { temperature: device.set_temperature - PASSO_TEMPERATURA })}
                      className="temp-btn"
                    >
                      -
                    </button>
                    <button
                      onClick={() => controlar(device, { temperature: device.set_temperature + PASSO_TEMPERATURA })}
                      className="temp-btn"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="device-info">
                  <div className="info-item">
                    <span className="info-label">{t('ac.modo')}</span>
                    <div className="mode-selector">
                      {MODOS.map(({ modo, icone, chave }) => (
                        <button
                          key={modo}
                          className={`mode-btn ${device.operation_mode === modo ? 'active' : ''}`}
                          onClick={() => controlar(device, { mode: modo })}
                          title={t(`ac.modos.${chave}`)}
                        >
                          {icone}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('ac.ventilacao')}</span>
                    <div className="fan-selector">
                      {VELOCIDADES.map(({ velocidade, chave }) => (
                        <button
                          key={velocidade}
                          className={`fan-btn ${device.fan_speed === velocidade ? 'active' : ''}`}
                          onClick={() => controlar(device, { fanSpeed: velocidade })}
                        >
                          {t(`ac.velocidades.${chave}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AirConditioning;
